/**
 * The member app's OtterAuth client (04-architecture § 11, server 06-auth § 2).
 *
 * The SDK owns the token in `localStorage`; `useSessionStore` mirrors it so the
 * API client's `getToken()` stays synchronous. `hydrateSession` runs once at
 * boot to bring the mirror up from a token that survived a reload.
 */

import { createAuth, localStorageTokenStorage } from '@cowoo/shared';
import { env } from '../env';
import { api } from './api';
import { useSessionStore } from './session';

export const auth = createAuth({
  publishableKey: env.OTTER_PUBLISHABLE_KEY ?? '',
  // § 5.4 — the key the server serves wins; the build-time one is the fallback.
  runtimeConfig: async () => {
    const { data, error } = await api.GET('/meta');
    if (error || !data) throw error ?? new Error('meta unavailable');
    return data.otter;
  },
  storage: localStorageTokenStorage(),
  // OtterAuth rejected the stored token: drop the mirror so the guards send the
  // user to `/login` instead of retrying with a token that is already dead.
  onSessionInvalidated: () => useSessionStore.getState().clear(),
});

/**
 * A stored token is only a claim. It is adopted straight away so the first
 * render is not a flash of the logged-out shell, and `me()` then confirms or
 * withdraws it — the user id comes from that call, since the token itself is
 * opaque and says nothing about who it belongs to.
 */
export async function hydrateSession(): Promise<void> {
  const token = auth.getToken();
  if (!token) return;

  useSessionStore.getState().setSession(token, '');
  try {
    const user = await auth.me();
    useSessionStore.getState().setSession(token, user.userId);
  } catch {
    // `onSessionInvalidated` already cleared an expired token; anything else
    // (OtterAuth unreachable) leaves the session in place — the API's own 401
    // handling is the second line of defence.
  }
}

/** The redirect target registered with OtterAuth for this app (06 § 1). */
export const OAUTH_REDIRECT_URI = `${env.MEMBER_URL}/auth/callback`;
